import React, { useState, useEffect, useCallback } from 'react'
import { FormattedMessage, injectIntl } from 'react-intl'
import PropTypes from 'prop-types'

import AppDispatcher from '../../../dispatchers/AppDispatcher'
import { NumberInput, SelectInput, TextInput, isNumber } from '../Inputs/Inputs'
import RedX from '../Graphics/RedX'
import styles from '../../../../css/settings.module.css'

const DEFAULT_PHYSICAL = {
  primaryPumpLabel: 'Primary Pump',
  secondaryPumpLabel: 'Secondary Pump',
  settlingTime: 300,
}

const APPLICATION_VALID = [
  { name: 'None', value: 0 },
  { name: 'Schedule', value: 1 },
]

const DEFAULT_SETTINGS = {
  application1: 0,
  application2: 0,
}

function Pump (props) {
  const defaultSettings = { settings: { ...DEFAULT_SETTINGS } }

  const {
    device: {
      application_settings: applicationSettings = defaultSettings,
      application_settings_new: applicationSettingsNew = defaultSettings,
      physical = DEFAULT_PHYSICAL,
    } = {},
    device,
    setValidity,
  } = props

  const {
    settings: {
      defaultApplication1 = DEFAULT_SETTINGS.application1,
      defaultApplication2 = DEFAULT_SETTINGS.application2,
    } = {},
  } = applicationSettings

  const {
    settings: {
      application1: newApplication1,
      application2: newApplication2,
    } = {},
  } = applicationSettingsNew

  const {
    primaryPumpLabel = DEFAULT_PHYSICAL.primaryPumpLabel,
    secondaryPumpLabel = DEFAULT_PHYSICAL.secondaryPumpLabel,
    settlingTime1: defaultSettlingTime = DEFAULT_PHYSICAL.settlingTime,
  } = physical

  const [formIsValid, setFormIsValid] = useState(true)
  const [invalidFields, setInvalidFields] = useState({})
  const [application1, setApplication1] = useState(newApplication1 || defaultApplication1)
  const [application2, setApplication2] = useState(newApplication2 || defaultApplication2)
  const [primaryPumpLabelState, setPrimaryPumpLabel] = useState(primaryPumpLabel)
  const [secondaryPumpLabelState, setSecondaryPumpLabel] = useState(secondaryPumpLabel)
  const [settlingTime, setSettlingTime] = useState(defaultSettlingTime)

  const appDispatch = useCallback(() => {
    const { address } = device

    const settings = {
      address,
      status: 'new',
      settings: {
        application1,
        application2,
      },
    }

    const physicals = {
      primaryPumpLabel: primaryPumpLabelState,
      secondaryPumpLabel: secondaryPumpLabelState,
      settlingTime1: settlingTime,
      settlingTime2: settlingTime,
    }

    AppDispatcher.dispatch({
      action: 'change-setting',
      setting: settings,
    })

    AppDispatcher.dispatch({
      action: 'change-physical',
      setting: physicals,
    })
  }, [device, application1, application2, primaryPumpLabelState, secondaryPumpLabelState, settlingTime])

  useEffect(() => {
    appDispatch()
  }, [application1, application2, primaryPumpLabelState, secondaryPumpLabelState, settlingTime])

  const childChange = useCallback((event) => {
    const { target: { name, value, validity: { valid } } } = event
    let newValue = value

    switch (name) {
      case 'application1':
        if (APPLICATION_VALID.find((item) => item.value === Number(value))) {
          newValue = Number(value)
        } else {
          newValue = DEFAULT_SETTINGS[name]
        }
        setApplication1(newValue)
        break
      case 'application2':
        if (APPLICATION_VALID.find((item) => item.value === Number(value))) {
          newValue = Number(value)
        } else {
          newValue = DEFAULT_SETTINGS[name]
        }
        setApplication2(newValue)
        break
      case 'primaryPumpLabel':
        setPrimaryPumpLabel(value)
        break
      case 'secondaryPumpLabel':
        setSecondaryPumpLabel(value)
        break
      case 'settlingTime':
        setSettlingTime(Number(value))
        break
      default:
        return
    }

    const newInvalidFields = { ...invalidFields }

    if (valid) {
      delete newInvalidFields[name]
    } else {
      newInvalidFields[name] = true
    }

    const newFormIsValid = Object.keys(newInvalidFields).length === 0

    setInvalidFields(newInvalidFields)
    setFormIsValid(newFormIsValid)
    setValidity(newFormIsValid)
  }, [invalidFields, setValidity, appDispatch])

  const unitStyle = { opacity: 0.7, marginBottom: '10px' }
  const redX = (
    <RedX
      style={{
        position: 'absolute',
        height: '20px',
        right: '4px',
        top: '8px',
        width: '20px',
      }}
    />
  )

  const textInputStyle = {
    height: '32px',
    position: 'relative',
    width: '100%',
  }

  const valueRequired = (value) => !value && redX
  const valueInRange = (
    value,
    min = 0,
    max = Number.MAX_VALUE
  ) => isNumber(value) && ((value < min) || (value > max)) && redX

  return (
    <div>
      <section className={styles.heading}>
        <h3>
          <FormattedMessage
            id='setting.pump.application.label'
            defaultMessage='Application Settings'
            description='Application Settings label'
          />
        </h3>
      </section>
      <div className={styles.inputArea}>
        <section>
          <h5>
            <FormattedMessage
              id='setting.pump.1.application.label'
              defaultMessage='Primary Pump Application'
              description='Primary Pump Application label'
            />
          </h5>
          <div className={styles.controlNumber}>
            <h4 className={styles.controlNumberLabel}>
              Label:
            </h4>
            <div style={textInputStyle}>
              <TextInput
                childChange={childChange}
                field='primaryPumpLabel'
                required
                style={styles.controlNumberInput}
                value={primaryPumpLabelState}
              />
              {valueRequired(primaryPumpLabelState)}
            </div>
          </div>
          <div className={styles.controlNumber}>
            <h4 className={styles.controlNumberLabel}>
              App:
            </h4>
            <SelectInput
              value={application1}
              options={APPLICATION_VALID}
              childChange={childChange}
              field='application1'
              style={styles.controlSelectInput}
            />
          </div>
        </section>
        <br />
        <section>
          <h5>
            <FormattedMessage
              id='setting.pump.2.application.label'
              defaultMessage='Secondary Pump Application'
              description='Secondary Pump Application label'
            />
          </h5>
          <div className={styles.controlNumber}>
            <h4 className={styles.controlNumberLabel}>
              Label:
            </h4>
            <div style={textInputStyle}>
              <TextInput
                childChange={childChange}
                field='secondaryPumpLabel'
                required
                style={styles.controlNumberInput}
                value={secondaryPumpLabelState}
              />
              {valueRequired(secondaryPumpLabelState)}
            </div>
          </div>
          <div className={styles.controlNumber}>
            <h4 className={styles.controlNumberLabel}>
              App:
            </h4>
            <SelectInput
              value={application2}
              options={APPLICATION_VALID}
              childChange={childChange}
              field='application2'
              style={styles.controlSelectInput}
            />
          </div>
        </section>
        <br />
        <section>
          <h5>
            <FormattedMessage
              id='setting.settlingTime.label'
              defaultMessage='Settling Time'
              description='Settling Time label'
            />
          </h5>
          <div className={styles.controlNumber} style={{ marginBottom: '0px' }}>
            <div style={textInputStyle}>
              <NumberInput
                value={settlingTime}
                childChange={childChange}
                field='settlingTime'
                max={6000}
                min={120}
                style={styles.controlNumberInput}
              />
              {valueInRange(settlingTime, 120, 6000)}
            </div>
          </div>
          <div style={unitStyle}>
            <div>
              <FormattedMessage
                id='units.seconds'
                defaultMessage='seconds'
              />
              &nbsp;
              (120 - 6000)
              &nbsp;
              [
              {Math.floor(settlingTime / 60)}
              &nbsp;
              <FormattedMessage
                id='units.minute.short'
                defaultMessage='min'
              />
              {
                (settlingTime % 60 > 0) && (
                  <span>
                    &nbsp;
                    -
                    &nbsp;
                    {settlingTime % 60}
                    &nbsp;
                    <FormattedMessage
                      id='units.second.short'
                      defaultMessage='sec'
                    />
                  </span>
                )
              }
              ]
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}

Pump.propTypes = {
  device: PropTypes.object.isRequired,
  setValidity: PropTypes.func.isRequired,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }),
}

export default injectIntl(Pump)
